import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Zap, Shield, FileText, Users, Star, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import SEO from '../components/SEO';

const PricingPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);

  const handlePurchase = async (packageId, stripeLink = null) => {
    try {
      setSelectedPackage(packageId);
      setLoading(true);
      
      // If package has a direct Stripe link (for promo codes), use that instead
      if (stripeLink) {
        window.location.href = stripeLink;
        return;
      }
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: `package_id=${packageId}`
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.url) {
        // Navigate to Stripe Checkout in the same tab
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }

    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout process. Please try again.');
      setLoading(false);
      setSelectedPackage(null);
    }
  };

  const packages = [
    {
      id: '0_500',
      name: 'Essential',
      description: 'Perfect for most cases',
      pages: '0-500 Pages',
      price: 39.99,
      features: [
        <span key="bold-feature" className="font-bold text-white">Ideal for targeted, high-precision analysis</span>,
        'Up to 500 pages analyzed',
        'Complete coercive control analysis',
        '19 category detection',
        'Professional PDF report',
        'Risk scoring & assessment',
        'Secure processing'
      ],
      popular: false,
      color: 'from-blue-500 to-cyan-500'
    },
    {
      id: '501_2500',
      name: 'Professional',
      description: 'For comprehensive analysis',
      pages: '501-2500 Pages',
      price: 59.99,
      features: [
        'Up to 2500 pages analyzed',
        'Complete coercive control analysis',
        '19 category detection',
        'Professional PDF report',
        'Risk scoring & assessment',
        'Priority processing',
        'Secure processing'
      ],
      popular: false,
      color: 'from-purple-500 to-pink-500'
    },
    {
      id: '2501_plus',
      name: 'Enterprise',
      description: 'For large-scale analysis',
      pages: '2501-5000 Pages',
      price: 89.99,
      features: [
        'Up to 5000 pages analyzed',
        'Complete coercive control analysis',
        '19 category detection',
        'Professional PDF report',
        'Risk scoring & assessment',
        'Highest priority processing',
        'Secure processing'
      ],
      popular: false,
      color: 'from-orange-500 to-red-500'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 page-transition">
      {/* Header */}
      <SEO 
        title="Pricing - Document Analysis Packages"
        description="Choose from our document analysis packages. Professional reports starting at $39.99. Analyze up to 5000 pages of communications for coercive control patterns."
        keywords="pricing, document analysis cost, coercive control report, pattern analysis pricing"
        canonical="/pricing"
      />
      <div className="bg-slate-800/50 backdrop-blur-sm border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 text-gray-300 hover:text-purple-400 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Home</span>
            </button>
            <div className="h-6 w-px bg-gray-600"></div>
            <h1 className="text-2xl font-bold gradient-text serif-display">Choose Your Package</h1>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-purple-600 via-indigo-600 to-blue-600 py-16">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 serif-display">
            Professional Analysis at Every Scale
          </h2>
          <p className="text-xl text-purple-100 max-w-3xl mx-auto">
            Choose the package that fits your needs. All packages include comprehensive 
            coercive control analysis with professional reporting.
          </p>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        {/* Important Notice */}
        <div className="mb-12 max-w-4xl mx-auto bg-slate-800/80 border-2 border-purple-500 rounded-xl p-6 md:p-8 animate-attention relative overflow-hidden">
          <div className="absolute top-0 left-0 w-2 h-full bg-gradient-to-b from-purple-500 to-pink-500"></div>
          <div className="flex flex-col space-y-4 text-center md:text-left">
            <h3 className="text-xl md:text-2xl font-bold text-white flex items-center justify-center md:justify-start gap-2">
              <AlertTriangle className="w-6 h-6 text-purple-400" />
              IMPORTANT: CHECK YOUR FILE SIZE BEFORE SELECTING A PACKAGE
            </h3>
            <p className="text-gray-300 text-sm md:text-base leading-relaxed">
              PDF page counts can vary depending on document type and formatting. Check the total size of your PDF file(s) and select the package that accommodates your total file size.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 py-4 border-y border-slate-700/50">
              <div className="bg-slate-900/50 p-3 rounded-lg text-center">
                <span className="block text-purple-400 font-semibold mb-1">Essential</span>
                <span className="text-white font-bold">Up to 10 MB</span>
              </div>
              <div className="bg-slate-900/50 p-3 rounded-lg text-center">
                <span className="block text-pink-400 font-semibold mb-1">Professional</span>
                <span className="text-white font-bold">Up to 30 MB</span>
              </div>
              <div className="bg-slate-900/50 p-3 rounded-lg text-center">
                <span className="block text-orange-400 font-semibold mb-1">Enterprise</span>
                <span className="text-white font-bold">Up to 50 MB</span>
              </div>
            </div>
            <p className="text-gray-300 text-sm md:text-base leading-relaxed">
              For more focused, detailed analysis, we recommend dividing very large communication records into smaller PDF files and uploading them separately.
            </p>
            <p className="text-gray-400 text-sm italic pt-2">
              To check your file size, right-click the PDF and select 'Properties' on Windows or 'Get Info' on Mac.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              className={`pricing-card relative ${pkg.popular ? 'popular transform scale-105' : ''}`}
            >
              {pkg.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-1 rounded-full text-sm font-semibold flex items-center space-x-1">
                    <Star className="w-4 h-4" />
                    <span>Most Popular</span>
                  </div>
                </div>
              )}

              <div className={`w-12 h-12 rounded-lg bg-gradient-to-r ${pkg.color} flex items-center justify-center mb-6 mx-auto`}>
                <FileText className="w-6 h-6 text-white" />
              </div>

              <h3 className="text-2xl font-bold text-center mb-2 text-white">{pkg.name}</h3>
              <p className="text-gray-300 text-center mb-4">{pkg.description}</p>
              
              <div className="text-center mb-6">
                <div className="text-4xl font-bold gradient-text mb-2">
                  ${pkg.price}
                </div>
                <div className="text-white font-bold text-lg">{pkg.pages}</div>
              </div>

              <ul className="space-y-3 mb-8">
                {pkg.features.map((feature, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <Check className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300 text-left">{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handlePurchase(pkg.id, pkg.stripeLink)}
                disabled={loading && selectedPackage === pkg.id}
                className={`w-full py-3 px-6 rounded-full font-semibold transition-all duration-300 ${
                  pkg.popular
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:shadow-lg'
                    : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                }`}
              >
                {loading && selectedPackage === pkg.id ? 'Processing...' : 'Get Started'}
              </button>
            </div>
          ))}
        </div>

      </div>

      {/* Important Disclaimer */}
      <div className="bg-amber-900/20 border-t border-b border-amber-600/30 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <AlertTriangle className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-amber-300 mb-2">Important Disclaimer</h3>
              <p className="text-amber-200">
                This tool uses advanced AI pattern detection to highlight behaviors linked to coercive control. While no AI system can guarantee 100% accuracy, the analysis is designed to be highly reliable and consistent across large sets of communication.
              </p>
              <p className="text-amber-200 mt-2">
                Reports should be viewed as supportive documentation — a way to make patterns measurable — not as final proof on their own. We recommend sharing results alongside your own records and professional guidance.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold mb-4 serif-display text-gray-800">What's Included in Every Package</h3>
            <p className="text-xl text-gray-700">
              Professional-grade analysis with comprehensive reporting
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Zap className="w-8 h-8 text-purple-600" />
              </div>
              <h4 className="text-xl font-semibold mb-3 text-gray-700">Advanced AI Analysis</h4>
              <p className="text-gray-700">
                Multiple AI models analyze your content using established psychological frameworks for comprehensive pattern detection.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <FileText className="w-8 h-8 text-purple-600" />
              </div>
              <h4 className="text-xl font-semibold mb-3 text-gray-700">Professional Reports</h4>
              <p className="text-gray-700">
                Detailed pattern PDF reports with comprehensive analysis, risk scoring, category breakdowns, and specific references.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Shield className="w-8 h-8 text-purple-600" />
              </div>
              <h4 className="text-xl font-semibold mb-3 text-gray-700">Secure & Confidential</h4>
              <p className="text-gray-700">
                Your data is processed securely with enterprise-grade encryption and permanently deleted immediately after report delivery.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Sample Report Section */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-3xl font-bold mb-6 serif-display text-gray-800">See a Sample Report</h3>
          <p className="text-xl text-gray-700 mb-8">
            Preview the comprehensive analysis and professional formatting included in every report.
          </p>
          
          <div className="bg-white rounded-2xl p-8 shadow-lg max-w-2xl mx-auto text-left">
            <h4 className="text-lg font-bold mb-4 text-center">📊 Make the Invisible Visible — Summary of Findings</h4>
            
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <strong>Total Pages Analyzed:</strong> 847
                </div>
                <div>
                  <strong>Total Instances:</strong> 156
                </div>
                <div>
                  <strong>Overall Risk Score:</strong> <span className="risk-high">High</span>
                </div>
                <div>
                  <strong>Post-Separation Abuse:</strong> 8/10
                </div>
              </div>
              
              <div className="border-t pt-4">
                <h5 className="font-semibold mb-2">Category Analysis Sample:</h5>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span>Gaslighting:</span>
                    <span>23 instances 🚩</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Manipulation:</span>
                    <span>31 instances</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Threats/Intimidation:</span>
                    <span>8 instances 🚩</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Analysis button removed - users must complete payment first */}
        </div>
      </div>

      {/* FAQ Section */}
      <div className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-3xl font-bold text-center mb-12 serif-display text-gray-800">Frequently Asked Questions</h3>
          
          <div className="space-y-8">
            <div>
              <h4 className="text-lg font-semibold mb-2 text-gray-700">How accurate is the analysis?</h4>
              <p className="text-gray-700">
                Our system uses multiple advanced AI models trained on established psychological frameworks. 
                While highly accurate for pattern detection, results should be reviewed by qualified professionals.
              </p>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-2 text-gray-700">How long does processing take?</h4>
              <p className="text-gray-700">
                Analysis typically takes 5-10 minutes for small files and an hour+ for larger files (500+ pages), depending on content length and package selected. 
                Higher-tier packages receive priority processing.
              </p>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-2 text-gray-700">Is my data secure?</h4>
              <p className="text-gray-700">
                Yes. All data is encrypted during processing. We do not retain user-uploaded files or analysis data beyond report delivery. 
                After the report is successfully downloaded, all associated files and data are permanently deleted.
              </p>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-2 text-gray-700">Can I use the reports in court?</h4>
              <p className="text-gray-700">
                Our reports are designed to be professional and comprehensive, but they are for informational 
                purposes only and not legal advice. Consult with qualified legal professionals for court use.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;